import type { SchemaData } from '../schema/types'
import { lineHexForTheme } from '../schema/lineColor'

export interface CarouselClusterCard {
  readonly id: string
  readonly name: string
  readonly lineLocalName: string
  readonly lineHexColor: string
  readonly zone: string | null
  /**
   * TFL NaPTAN id — used to resolve the live status snapshot (spec §6).
   * Optional: stations without a naptanId (e.g. non-TfL stations) are
   * always shown as Open.
   */
  readonly naptanId?: string
}

/**
 * BFS the transfer-station graph starting from `currentId` and return the
 * connected component as a stable, id-sorted list of card descriptors.
 *
 * Why BFS instead of just `[current, ...current.transferStationsIds]`:
 * `transferStationsIds` is asymmetric (e.g. Euston Victoria-line lists
 * BOTH Northern platforms; each Northern platform lists only the OTHER
 * line, not its same-line sibling). Without the closure, cluster
 * cardinality oscillates 3↔4 between cluster-internal selections,
 * slot positions shift, and a settle-loop kicks in. The closure is
 * identical for every member of the component → carousel is stable.
 *
 * Returns [] if currentId is missing from `schema.stations`.
 */
export function buildCarouselCluster(
  schema: SchemaData,
  currentId: string,
  theme: 'light' | 'dark',
): readonly CarouselClusterCard[] {
  // Build an id → station Map once (O(n)) to avoid repeated linear
  // `Array.find` scans inside the BFS body and the post-sort lookup.
  // For the full London schema (~700 stations) this turns three nested
  // O(n) lookups into O(1) per id touched.
  const byId = new Map(schema.stations.map((s) => [s.id, s]))
  const current = byId.get(currentId)
  if (current === undefined) return []
  const visited = new Set<string>()
  // Index-pointer "queue" — `Array.shift()` is O(n) per call; for tiny
  // (≤5 station) interchange clusters this is irrelevant, but keeps
  // the closure body O(n) overall in case the helper later sees larger
  // graphs (multi-line interchange complexes, nat-rail integration).
  const queue: string[] = [current.id]
  for (let head = 0; head < queue.length; head++) {
    const id = queue[head]
    if (visited.has(id)) continue
    visited.add(id)
    const st = byId.get(id)
    if (st === undefined) continue
    for (const tid of st.transferStationsIds ?? []) {
      if (!visited.has(tid)) queue.push(tid)
    }
  }
  const sorted = Array.from(visited).sort()
  return sorted
    .map((id) => byId.get(id))
    .filter((s): s is NonNullable<typeof s> => s !== undefined)
    .map((s) => ({
      id: s.id,
      name: s.localName,
      lineLocalName: s.line.localName ?? '',
      lineHexColor: lineHexForTheme(s.line, theme),
      // Schema's `zone` is `string | undefined`; normalize to null at
      // the boundary so consumers don't have to handle two falsy types.
      zone: s.zone ?? null,
      // naptanId is optional on schema stations (pre-§13 fixtures may omit it).
      naptanId: s.naptanId,
    }))
}
